import {
  RichTextOption,
  richTextOptions,
  tiptapNodeToRichTextOption,
} from '@basehub/tiptap-api-utils/options'
import LinterPlugin, { Result } from '../plugin'
import * as React from 'react'
import tippy, { Instance } from 'tippy.js'
import { title } from '~/lib/title'

export class AllowedFormatting extends LinterPlugin {
  scan() {
    this.doc.descendants((node: any, position: number) => {
      const allowedFormatting = (this.props as any).allowedFormatting as
        | string[]
        | null
      if (!allowedFormatting) return

      // if allowedNodes is defined, we need to check if the node is allowed
      const nodeTypes = tiptapNodeToRichTextOption(node)
      const notAllowedNodes = nodeTypes
        .map((n: any) => {
          const nodeName = 'name' in n ? n.name : n
          const format =
            nodeName === 'heading' ? `h${node.attrs.level}` : nodeName

          if (
            richTextOptions.includes(format) &&
            !allowedFormatting.includes(format)
          ) {
            return format
          }
          return null
        })
        .filter(Boolean)

      if (notAllowedNodes.length) {
        let type: Result['type'] = 'inline'
        let from = position
        let to = position + (node.content?.size || node.text?.length || 1)
        notAllowedNodes.forEach((n: RichTextOption) => {
          switch (n) {
            case 'image':
            case 'video':
              type = 'node'
              break
            case 'table':
            case 'codeBlock':
              type = 'node'
              to = to + 2
              break
            case 'h1':
            case 'h2':
            case 'h3':
              from = from + 0
              to = to + 1
              break
            default:
              break
          }
        })
        this.record(
          'error',
          `${notAllowedNodes
            .map((n, i, { length }) => {
              const capitalized = title(n)
              if (length === 1) return capitalized
              if (i === length - 1 && length > 1) return `and ${capitalized}`
              if (i === length - 2) return `${capitalized} `
              return `${capitalized}, `
            })
            .join('')} formatting ${
            notAllowedNodes.length > 1 ? 'are' : 'is'
          } disabled`,
          from,
          to,
          type
        )
      }
    })

    return this
  }
}

export const useAllowedFormattingLint = ({
  editorElement,
  blockHash,
}: {
  editorElement: HTMLElement | undefined
  blockHash: string
}) => {
  React.useEffect(() => {
    if (!editorElement) return

    const activeTooltipsMap = new Map<string, Instance>()

    function onPointerMove(e: PointerEvent) {
      let activeIssue: (Result & { el: HTMLElement; id: string }) | null = null

      if (e.target instanceof HTMLElement) {
        const issue = e.target.closest('.pm-linter-problem')
        if (issue && issue instanceof HTMLElement) {
          const from = parseInt(issue.dataset.issueFrom ?? '0')
          const to = parseInt(issue.dataset.issueTo ?? '0')
          activeIssue = {
            id: `${from}_${to}`,
            el: issue,
            from,
            to,
            message: issue.dataset.issueMessage ?? '',
            type: issue.dataset.issueType as 'node' | 'inline',
            level: issue.dataset.issueLevel as
              | 'error'
              | 'warning'
              | 'suggestion',
          }
        }
      }

      for (const [id, tooltip] of activeTooltipsMap.entries()) {
        if (id === activeIssue?.id) continue
        tooltip.destroy()
        activeTooltipsMap.delete(id)
      }

      if (activeIssue) {
        const activeTooltip =
          activeTooltipsMap.get(activeIssue.id) ??
          tippy(activeIssue.el, {
            trigger: 'manual',
            content: `<span class="linter-tooltip">${activeIssue.message}</span>`,
            allowHTML: true,
            appendTo: editorElement?.parentElement ?? document.body,
          })
        activeTooltipsMap.set(activeIssue.id, activeTooltip)
        if (activeTooltip.state.isVisible) return
        activeTooltip.show()
      }
    }

    editorElement.addEventListener('pointermove', onPointerMove)

    return () => {
      editorElement.removeEventListener('pointermove', onPointerMove)
      for (const tooltip of activeTooltipsMap.values()) {
        tooltip.destroy()
      }
    }
  }, [editorElement, blockHash])

  return null
}
